(() => {
  const CONFIG_ELEMENT_ID = 'klaviyo-popup-trigger-config';
  const TRIGGER_ELEMENT_ID = 'klaviyo-popup-trigger';
  const LAST_FORM_ID_STORAGE_KEY = 'mora-klaviyo-last-popup-form-id';
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  const DEFAULTS = {
    enabled: false,
    formId: '',
    label: 'Save 10%',
    ariaLabel: 'Open signup form',
    zIndex: 2147483000,
    bottomGapDesktop: 8,
    bottomGapMobile: 8,
    sideGapDesktop: 24,
    sideGapMobile: 12,
  };
  const FORM_ID_PLACEHOLDERS = new Set(['PLACEHOLDER', 'FORM_ID', 'YOUR_FORM_ID']);
  const ALIGNMENT_NUDGE_DESKTOP_PX = 0;
  const ALIGNMENT_NUDGE_MOBILE_PX = -2;
  const CONSENTMO_HOST_SELECTOR = '.csm-cookie-consent';
  const ADA_HOST_SELECTOR = 'csm-web-accessibility';
  const CONSENTMO_BANNER_SELECTORS = [
    '.isense-cookieconsent-wrapper',
    '.csm-cookie-box',
    '.csm-dialog',
    '.cc-window',
    '.isense-cc-window',
  ];
  const DOCUMENT_BOTTOM_OVERLAY_SELECTORS = [
    '#shopify-pc__banner',
    '.shopify-pc__banner',
    '.cc-window',
    '.isense-cc-window',
  ];
  const LEFT_CONTROL_SELECTORS = [
    '#csm-ada-compliance-wrapper button',
    '#csm-ada-compliance-wrapper [role="button"]',
    '.isense-reopen-widget-button',
    '.isense-reopen-widget-link',
    '#csm-ada-compliance-wrapper',
    '.csm-ada-compliance',
    '.csm-accessibility-link',
  ];
  const FORM_CLOSE_EVENT_TYPES = new Set(['close', 'redirectedToUrl']);

  let config = null;
  let triggerRoot = null;
  let isQueuedForUpdate = false;
  let lastKnownAlignedBottom = null;
  let lastKnownAlignedTop = null;

  function getViewportHeight() {
    const visualViewport = window.visualViewport;
    if (visualViewport && Number.isFinite(visualViewport.height) && visualViewport.height > 0) {
      return visualViewport.height;
    }

    return window.innerHeight;
  }

  function getNumericConfigValue(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function normalizeConfig(rawConfig) {
    const normalized = Object.assign({}, DEFAULTS, rawConfig);
    normalized.enabled = Boolean(normalized.enabled);
    normalized.formId = normalizeFormId(normalized.formId);
    normalized.label = typeof normalized.label === 'string' && normalized.label.trim() ? normalized.label.trim() : DEFAULTS.label;
    normalized.ariaLabel =
      typeof normalized.ariaLabel === 'string' && normalized.ariaLabel.trim()
        ? normalized.ariaLabel.trim()
        : normalized.label;
    normalized.zIndex = getNumericConfigValue(normalized.zIndex, DEFAULTS.zIndex);
    normalized.bottomGapDesktop = getNumericConfigValue(normalized.bottomGapDesktop, DEFAULTS.bottomGapDesktop);
    normalized.bottomGapMobile = getNumericConfigValue(normalized.bottomGapMobile, DEFAULTS.bottomGapMobile);
    normalized.sideGapDesktop = getNumericConfigValue(normalized.sideGapDesktop, DEFAULTS.sideGapDesktop);
    normalized.sideGapMobile = getNumericConfigValue(normalized.sideGapMobile, DEFAULTS.sideGapMobile);

    return normalized;
  }

  function normalizeFormId(value) {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return '';
    }

    return FORM_ID_PLACEHOLDERS.has(normalized.toUpperCase()) ? '' : normalized;
  }

  function getConfig() {
    const configElement = document.getElementById(CONFIG_ELEMENT_ID);
    if (!configElement?.textContent) {
      return normalizeConfig(null);
    }

    try {
      const parsedConfig = JSON.parse(configElement.textContent);
      return normalizeConfig(parsedConfig);
    } catch (error) {
      console.error('Klaviyo popup trigger: invalid config JSON.', error);
      return normalizeConfig(null);
    }
  }

  function isElementVisible(element) {
    if (!(element instanceof Element)) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number.parseFloat(style.opacity) === 0) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function getBottomOverlap(element) {
    if (!isElementVisible(element)) {
      return 0;
    }

    const style = window.getComputedStyle(element);
    const isPositionedAtViewport = style.position === 'fixed' || style.position === 'sticky';
    if (!isPositionedAtViewport) {
      return 0;
    }

    const viewportHeight = getViewportHeight();
    const rect = element.getBoundingClientRect();
    if (rect.bottom < viewportHeight - 1) {
      return 0;
    }

    return Math.max(0, viewportHeight - Math.max(0, rect.top));
  }

  function getConsentmoBottomOverlap() {
    const host = document.querySelector(CONSENTMO_HOST_SELECTOR);
    if (!(host instanceof HTMLElement)) {
      return 0;
    }

    let overlap = 0;
    const shadowRoot = host.shadowRoot;
    if (shadowRoot) {
      CONSENTMO_BANNER_SELECTORS.forEach((selector) => {
        shadowRoot.querySelectorAll(selector).forEach((element) => {
          overlap = Math.max(overlap, getBottomOverlap(element));
        });
      });
    }

    if (overlap === 0) {
      const hostOverlap = getBottomOverlap(host);
      if (hostOverlap > 0 && hostOverlap < getViewportHeight() * 0.75) {
        overlap = hostOverlap;
      }
    }

    return overlap;
  }

  function getDocumentBottomOverlap() {
    let overlap = 0;
    DOCUMENT_BOTTOM_OVERLAY_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        overlap = Math.max(overlap, getBottomOverlap(element));
      });
    });
    return overlap;
  }

  function getCandidateControlRects(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return [];
    }

    const viewportHeight = getViewportHeight();
    const candidates = [];
    LEFT_CONTROL_SELECTORS.forEach((selector) => {
      root.querySelectorAll(selector).forEach((element) => {
        if (!isElementVisible(element)) {
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.width < 20 || rect.height < 20) {
          return;
        }

        if (rect.width > 120 || rect.height > 120) {
          return;
        }

        if (rect.left > window.innerWidth * 0.35 || rect.top < viewportHeight * 0.45) {
          return;
        }

        candidates.push(rect);
      });
    });

    return candidates;
  }

  function getBottomLeftSampleRectsFromDocument() {
    if (typeof document.elementsFromPoint !== 'function') {
      return [];
    }

    const viewportHeight = getViewportHeight();
    const rects = [];
    const seen = new Set();
    const sampleXs = [16, 36, 56, 84, 112, 140];
    const sampleYs = [viewportHeight - 16, viewportHeight - 36, viewportHeight - 56, viewportHeight - 86];

    sampleYs.forEach((y) => {
      sampleXs.forEach((x) => {
        document.elementsFromPoint(x, y).forEach((element) => {
          if (!(element instanceof HTMLElement)) {
            return;
          }

          const chain = [element, element.parentElement, element.parentElement?.parentElement].filter(Boolean);
          chain.forEach((candidate) => {
            if (!(candidate instanceof HTMLElement)) {
              return;
            }

            if (seen.has(candidate)) {
              return;
            }

            if (!isElementVisible(candidate)) {
              return;
            }

            const style = window.getComputedStyle(candidate);
            if (!['fixed', 'sticky'].includes(style.position)) {
              return;
            }

            const rect = candidate.getBoundingClientRect();
            if (rect.width < 20 || rect.height < 20 || rect.width > 180 || rect.height > 180) {
              return;
            }

            if (rect.left > window.innerWidth * 0.4 || rect.top < viewportHeight * 0.45) {
              return;
            }

            seen.add(candidate);
            rects.push(rect);
          });
        });
      });
    });

    return rects;
  }

  function getLeftControlAlignment(triggerHeight, alignmentNudgePx) {
    if (!triggerHeight) {
      return null;
    }

    const allRects = [];
    allRects.push(...getCandidateControlRects(document));

    const consentmoHost = document.querySelector(CONSENTMO_HOST_SELECTOR);
    if (consentmoHost?.shadowRoot) {
      allRects.push(...getCandidateControlRects(consentmoHost.shadowRoot));
    }

    const adaHost = document.querySelector(ADA_HOST_SELECTOR);
    if (adaHost?.shadowRoot) {
      allRects.push(...getCandidateControlRects(adaHost.shadowRoot));
    }

    if (allRects.length === 0) {
      allRects.push(...getBottomLeftSampleRectsFromDocument());
    }

    if (allRects.length === 0) {
      return null;
    }

    const preferredRects = allRects.filter((rect) => {
      const isSmallButton = rect.width >= 34 && rect.width <= 88 && rect.height >= 34 && rect.height <= 88;
      const aspectCloseToSquare = Math.abs(rect.width - rect.height) <= 22;
      return isSmallButton && aspectCloseToSquare;
    });
    const targetRects = preferredRects.length > 0 ? preferredRects : allRects;

    const averageCenterY =
      targetRects.reduce((sum, rect) => sum + (rect.top + rect.height / 2), 0) / targetRects.length;
    const viewportHeight = getViewportHeight();
    const alignedBottom = Math.max(0, viewportHeight - averageCenterY - triggerHeight / 2 + alignmentNudgePx);
    const alignedTop = Math.max(0, averageCenterY - triggerHeight / 2 + alignmentNudgePx);

    return {
      alignedBottom,
      alignedTop,
    };
  }

  function updateTriggerPosition() {
    if (!triggerRoot || !config) {
      return;
    }

    const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    const alignmentNudgePx = isMobile ? ALIGNMENT_NUDGE_MOBILE_PX : ALIGNMENT_NUDGE_DESKTOP_PX;
    const baseBottomGap = isMobile ? config.bottomGapMobile : config.bottomGapDesktop;
    const sideGap = isMobile ? config.sideGapMobile : config.sideGapDesktop;
    const bottomOverlap = Math.max(getConsentmoBottomOverlap(), getDocumentBottomOverlap());
    const triggerHeight = triggerRoot.getBoundingClientRect().height || triggerRoot.offsetHeight || 0;
    const alignment = getLeftControlAlignment(triggerHeight, alignmentNudgePx);
    if (alignment && typeof alignment.alignedBottom === 'number') {
      lastKnownAlignedBottom = alignment.alignedBottom;
    }
    if (alignment && typeof alignment.alignedTop === 'number') {
      lastKnownAlignedTop = alignment.alignedTop;
    }
    const effectiveAlignedBottom =
      alignment && typeof alignment.alignedBottom === 'number' ? alignment.alignedBottom : lastKnownAlignedBottom;
    const effectiveAlignedTop =
      alignment && typeof alignment.alignedTop === 'number' ? alignment.alignedTop : lastKnownAlignedTop;
    const resolvedBottom =
      typeof effectiveAlignedBottom === 'number'
        ? Math.max(0, effectiveAlignedBottom)
        : Math.max(baseBottomGap + bottomOverlap, baseBottomGap);

    if (isMobile && typeof effectiveAlignedTop === 'number') {
      triggerRoot.classList.add('is-mobile-top-aligned');
      triggerRoot.style.top = `${Math.max(0, effectiveAlignedTop)}px`;
    } else {
      triggerRoot.classList.remove('is-mobile-top-aligned');
      triggerRoot.style.removeProperty('top');
    }

    triggerRoot.style.setProperty('--klaviyo-popup-trigger-z-index', String(config.zIndex));
    triggerRoot.style.setProperty('--klaviyo-popup-trigger-right', `${sideGap}px`);
    triggerRoot.style.setProperty('--klaviyo-popup-trigger-bottom', `${resolvedBottom}px`);
  }

  function queuePositionUpdate() {
    if (isQueuedForUpdate) {
      return;
    }

    isQueuedForUpdate = true;
    window.requestAnimationFrame(() => {
      isQueuedForUpdate = false;
      updateTriggerPosition();
    });
  }

  function readStoredFormId() {
    try {
      const value = window.localStorage.getItem(LAST_FORM_ID_STORAGE_KEY);
      return normalizeFormId(value);
    } catch (error) {
      return '';
    }
  }

  function storeFormId(formId) {
    const normalized = normalizeFormId(formId);
    if (!normalized) {
      return;
    }

    try {
      window.localStorage.setItem(LAST_FORM_ID_STORAGE_KEY, normalized);
    } catch (error) {
      /* no-op */
    }
  }

  function getFormIdFromEventDetail(detail) {
    if (!detail || typeof detail !== 'object') {
      return '';
    }

    const formId = detail.formId || detail.formID || detail.form_id;
    return normalizeFormId(formId);
  }

  function getResolvedFormId() {
    return config.formId || readStoredFormId();
  }

  function openKlaviyoForm(formId) {
    if (!formId) {
      return false;
    }

    window._klOnsite = window._klOnsite || [];

    try {
      if (typeof window._klOnsite.push === 'function') {
        window._klOnsite.push(['openForm', formId]);
        return true;
      }

      if (Array.isArray(window._klOnsite)) {
        window._klOnsite.push(['openForm', formId]);
        return true;
      }

      if (typeof window._klOnsite.openForm === 'function') {
        window._klOnsite.openForm([formId]);
        return true;
      }
    } catch (error) {
      console.error('Klaviyo popup trigger: failed to open form.', error);
    }

    return false;
  }

  function clickFallbackTrigger() {
    const fallbackTrigger = document.querySelector('.klaviyo_form_trigger, [data-klaviyo-form-trigger]');
    if (!(fallbackTrigger instanceof HTMLElement)) {
      return false;
    }

    fallbackTrigger.click();
    return true;
  }

  function handleTriggerClick() {
    const formId = getResolvedFormId();
    const opened = openKlaviyoForm(formId) || clickFallbackTrigger();

    if (!opened) {
      console.warn('Klaviyo popup trigger: missing form ID. Set it in Theme settings > Klaviyo > Popup Form ID.');
    }
  }

  function handleKlaviyoFormsEvent(event) {
    const detail = event?.detail;
    const formId = getFormIdFromEventDetail(detail);
    if (formId) {
      storeFormId(formId);
    }

    if (!triggerRoot || !detail || typeof detail.type !== 'string') {
      return;
    }

    if (detail.type === 'open') {
      triggerRoot.classList.add('is-form-open');
      return;
    }

    if (FORM_CLOSE_EVENT_TYPES.has(detail.type)) {
      triggerRoot.classList.remove('is-form-open');
    }
  }

  function mountTrigger() {
    const existingTrigger = document.getElementById(TRIGGER_ELEMENT_ID);
    if (existingTrigger instanceof HTMLElement) {
      triggerRoot = existingTrigger;
      return;
    }

    triggerRoot = document.createElement('div');
    triggerRoot.id = TRIGGER_ELEMENT_ID;
    triggerRoot.className = 'klaviyo-popup-trigger';

    const triggerButton = document.createElement('button');
    triggerButton.type = 'button';
    triggerButton.className = 'klaviyo-popup-trigger__button';
    triggerButton.textContent = config.label;
    triggerButton.setAttribute('aria-label', config.ariaLabel);
    triggerButton.addEventListener('click', handleTriggerClick);

    triggerRoot.appendChild(triggerButton);
    document.body.appendChild(triggerRoot);
  }

  function initializeObservers() {
    window.addEventListener('resize', queuePositionUpdate, { passive: true });
    window.addEventListener('orientationchange', queuePositionUpdate, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', queuePositionUpdate, { passive: true });
      window.visualViewport.addEventListener('scroll', queuePositionUpdate, { passive: true });
    }
    document.addEventListener('consentmoSignal_onLoad', queuePositionUpdate);
    document.addEventListener('consentmoSignal', queuePositionUpdate);
    window.addEventListener('klaviyoForms', handleKlaviyoFormsEvent);

    const observer = new MutationObserver(queuePositionUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.setInterval(queuePositionUpdate, 500);
  }

  function initialize() {
    config = getConfig();
    if (!config.enabled) {
      return;
    }

    mountTrigger();
    if (!triggerRoot) {
      return;
    }

    initializeObservers();
    queuePositionUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
